// Integrador Runge-Kutta de 4.º orden para un cuerpo rígido. Ver docs/theory/simulation.md.
import { q as Q, type Quat } from '../math';
import { type Vec3, addScaled, scale, vec3 } from '../math/vec3';

export interface BodyState {
  /** Tiempo (s). */
  readonly t: number;
  /** Posición, ejes mundo (m). */
  readonly pos: Vec3;
  /** Velocidad, ejes mundo (m/s). */
  readonly vel: Vec3;
  /** Orientación cuerpo → mundo. */
  readonly q: Quat;
  /** Velocidad angular, ejes del cuerpo (rad/s). */
  readonly omega: Vec3;
}

export interface Derivatives {
  /** Aceleración lineal, ejes mundo (m/s²). */
  readonly acc: Vec3;
  /** Aceleración angular, ejes del cuerpo (rad/s²). */
  readonly alpha: Vec3;
}

export type DerivativeFn = (s: BodyState) => Derivatives;

/** Avanza el cuaternión una rotación expresada en ejes del cuerpo: q ∘ exp(rv). */
export const advanceOrientation = (q: Quat, rotationVector: Vec3): Quat => Q.normalizeIfNeeded(Q.multiply(q, Q.fromRotationVector(rotationVector)));

/**
 * Un paso RK4 de duración `h`. Posición, velocidad y velocidad angular siguen el esquema clásico;
 * la orientación se compone con un único vector de rotación `h/6 (ω₁ + 2ω₂ + 2ω₃ + ω₄)`.
 */
export function rk4Step(s: BodyState, h: number, f: DerivativeFn): BodyState {
  const k1 = f(s);
  const w1 = s.omega;
  const v1 = s.vel;

  const s2: BodyState = {
    t: s.t + h / 2,
    pos: addScaled(s.pos, v1, h / 2),
    vel: addScaled(s.vel, k1.acc, h / 2),
    omega: addScaled(s.omega, k1.alpha, h / 2),
    q: advanceOrientation(s.q, scale(w1, h / 2)),
  };
  const k2 = f(s2);
  const w2 = s2.omega;
  const v2 = s2.vel;

  const s3: BodyState = {
    t: s.t + h / 2,
    pos: addScaled(s.pos, v2, h / 2),
    vel: addScaled(s.vel, k2.acc, h / 2),
    omega: addScaled(s.omega, k2.alpha, h / 2),
    q: advanceOrientation(s.q, scale(w2, h / 2)),
  };
  const k3 = f(s3);
  const w3 = s3.omega;
  const v3 = s3.vel;

  const s4: BodyState = {
    t: s.t + h,
    pos: addScaled(s.pos, v3, h),
    vel: addScaled(s.vel, k3.acc, h),
    omega: addScaled(s.omega, k3.alpha, h),
    q: advanceOrientation(s.q, scale(w3, h)),
  };
  const k4 = f(s4);
  const w4 = s4.omega;
  const v4 = s4.vel;

  const sum = (a: Vec3, b: Vec3, c: Vec3, d: Vec3): Vec3 =>
    vec3(a.x + 2 * b.x + 2 * c.x + d.x, a.y + 2 * b.y + 2 * c.y + d.y, a.z + 2 * b.z + 2 * c.z + d.z);

  return {
    t: s.t + h,
    pos: addScaled(s.pos, sum(v1, v2, v3, v4), h / 6),
    vel: addScaled(s.vel, sum(k1.acc, k2.acc, k3.acc, k4.acc), h / 6),
    omega: addScaled(s.omega, sum(k1.alpha, k2.alpha, k3.alpha, k4.alpha), h / 6),
    q: advanceOrientation(s.q, scale(sum(w1, w2, w3, w4), h / 6)),
  };
}
